import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpen,
  Sunrise,
  Flame,
  PenLine,
  Users,
} from "lucide-react";
import useLenis from "@/hooks/useLenis";
import useSEO from "@/hooks/useSEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLARS = [
  {
    n: "I",
    Icon: BookOpen,
    title: "Daily Bread",
    quote: "Man shall not live by bread alone…",
    lead: "Your spiritual growth comes first.",
    body: "Every month you will read one complete book of the Bible with a written reflection covering the main message, key lessons, and personal application. Then read one personal-development book from the approved list and file a one-page report.",
    list: {
      label: "Featured Authors",
      items: [
        "Robert Greene",
        "David Goggins",
        "Jocko Willink",
        "James Allen",
        "Jason Wilson",
        "Jackson Steel",
        "George S. Clason",
      ],
    },
  },
  {
    n: "II",
    Icon: Sunrise,
    title: "Daily Consistency",
    quote: "Transformation happens through daily habits.",
    lead: "Consistency creates identity.",
    body: "Every day — non-negotiable. Lift weights (5+ days/week), complete cardiovascular training, stretch 15 minutes minimum, spend 30 minutes with God, and develop one new skill.",
    list: {
      label: "Skill Examples",
      items: [
        "Learning a language",
        "Playing an instrument",
        "Building a business",
        "Learning to code",
        "Public speaking",
        "Financial education",
      ],
    },
  },
  {
    n: "III",
    Icon: Flame,
    title: "Fat & Nasty Challenge",
    quote: "The only goal is to finish.",
    lead: "A new challenge released every week through MBS Overload.",
    body: "Every week you'll face a new challenge designed to push your limits. Show up, start, and finish.",
    list: {
      label: "Sample Challenges",
      items: [
        "24-hour fast",
        "21-mile walk",
        "5K and 10K runs",
        "100 deadlift reps",
        "Murph Challenge",
        "400 push-ups",
        "10,000 jump ropes",
        "100 burpees",
        "4:00 AM wake-up",
        "100 pull-ups",
        "Farmers carries",
        "Warrior Challenge",
        "One mile every hour · 24 hours",
      ],
    },
  },
  {
    n: "IV",
    Icon: PenLine,
    title: "Your Book",
    quote: "By Day 180, you will have written the story of who you became.",
    lead: "Every week you write the next chapter of your own life.",
    body: "Over 24 weeks you'll answer powerful questions and build your personal transformation journal — a book only you could have written.",
    list: {
      label: "Chapter Themes",
      items: [
        "Who am I?",
        "Who do I want to become?",
        "My relationship with God",
        "Honesty",
        "Fear",
        "Purpose",
        "Character",
        "Mental health",
        "Physical health",
        "Relationships",
        "Gratitude",
        "Identity",
        "Forgiveness",
        "Discipline",
        "Goals",
        "Sacrifice",
        "Legacy",
      ],
    },
  },
  {
    n: "V",
    Icon: Users,
    title: "Ultimate Warrior",
    quote: "Transformation deserves accountability.",
    lead: "Post your progress every week.",
    body: "Share your journey through MBS Overload. Show your victories, own your failures, and inspire someone to begin theirs.",
    list: {
      label: "You Track",
      items: [
        "Faith",
        "Fitness",
        "Discipline",
        "Mindset",
        "Physical transformation",
        "Mental growth",
        "Spiritual growth",
      ],
    },
  },
];

const GAINS = [
  {
    label: "Physical",
    items: [
      "Increased strength",
      "Improved endurance",
      "Better health",
      "Greater confidence",
    ],
  },
  {
    label: "Mental",
    items: [
      "Stronger discipline",
      "Increased resilience",
      "Better habits",
      "Sharper focus",
    ],
  },
  {
    label: "Spiritual",
    items: [
      "Deeper relationship with God",
      "Daily Scripture study",
      "Consistent prayer",
      "Stronger faith",
    ],
  },
  {
    label: "Character",
    items: [
      "Integrity",
      "Humility",
      "Courage",
      "Honesty",
      "Leadership",
      "Accountability",
    ],
  },
];

const COVENANT = [
  "I commit to becoming the person God created me to be.",
  "I choose discipline over excuses.",
  "I choose faith over fear.",
  "I choose honesty over comfort.",
  "I choose growth over complacency.",
  "I choose consistency over motivation.",
  "I refuse to quit.",
  "For the next 180 days, I will pursue excellence in my Mind, Body, and Soul.",
];

const CommitForm = () => {
  const [form, setForm] = useState({ name: "", email: "", goal: "" });
  const [status, setStatus] = useState("idle");
  const [numberedId, setNumberedId] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      toast.error("Name and a valid email are required to sign the covenant.");
      return;
    }
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/challenge/commit`, {
        name: form.name,
        email: form.email,
        goal: form.goal,
      });
      setNumberedId(res.data?.numbered_id || "");
      setStatus("success");
      toast.success(`Covenant sealed. ${res.data?.numbered_id || ""}`);
    } catch (err) {
      setStatus("idle");
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something broke. Try again.";
      toast.error(typeof msg === "string" ? msg : "Commit failed.");
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border border-silver-100/40 rounded-md p-8 md:p-12 bg-white/[0.03] text-center"
      >
        <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-4">
          §— COVENANT SEALED
        </div>
        <div className="font-serif italic text-4xl md:text-6xl text-silver-100 mb-4">
          Day 1 begins tomorrow.
        </div>
        <p className="text-white/65 leading-[1.75] max-w-lg mx-auto">
          Your covenant has been recorded. In 180 days, we&apos;ll ask you what
          changed. Until then — the work, the silence, the discipline. Show up.
        </p>
        <div className="mt-6 font-mono text-[11px] tracking-[0.36em] text-white">
          YOUR NUMBER — {numberedId}
        </div>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="challenge-commit-form"
      className="border border-white/10 rounded-md p-8 md:p-12 bg-white/[0.02]"
    >
      <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-3">
        §— SIGN THE COVENANT
      </div>
      <h3 className="font-serif text-white text-3xl md:text-5xl leading-[1.02] mb-3">
        Sign your <span className="italic text-silver-100">name</span> to the
        next 180 days.
      </h3>
      <p className="text-white/55 text-[14px] leading-[1.75] mb-8 max-w-xl">
        Free to enlist. No payment. Your inbox receives Day 1 at dawn, weekly
        challenges, and the reading list.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <label className="block">
          <span className="font-mono text-[10px] tracking-[0.35em] text-silver-300">
            NAME
          </span>
          <input
            required
            type="text"
            data-testid="challenge-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-silver-100 outline-none text-white py-2 text-[15px]"
            placeholder="Your full name"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] tracking-[0.35em] text-silver-300">
            EMAIL
          </span>
          <input
            required
            type="email"
            data-testid="challenge-email-input"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-silver-100 outline-none text-white py-2 text-[15px]"
            placeholder="you@dispatch.com"
          />
        </label>
      </div>

      <label className="block mt-6">
        <span className="font-mono text-[10px] tracking-[0.35em] text-silver-300">
          WHY 180 DAYS?
        </span>
        <textarea
          data-testid="challenge-goal-input"
          value={form.goal}
          onChange={(e) => setForm({ ...form, goal: e.target.value })}
          rows={3}
          className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-silver-100 outline-none text-white py-2 text-[15px] resize-none"
          placeholder="Write it. You'll read it in 180 days."
        />
      </label>

      <button
        type="submit"
        data-testid="challenge-commit-submit"
        disabled={status === "loading"}
        className="mt-10 inline-flex items-center gap-3 px-8 py-4 rounded-full bg-silver-100 text-black hover:bg-white transition-colors duration-500 disabled:opacity-60"
      >
        <span className="text-[11px] tracking-[0.32em] uppercase font-medium">
          {status === "loading" ? "Sealing…" : "Sign the Covenant"}
        </span>
        <span className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 6H11M11 6L6 1M11 6L6 11" stroke="currentColor" strokeWidth="1.2" />
          </svg>
        </span>
      </button>
    </form>
  );
};

const Turn180 = () => {
  useLenis();
  useSEO({
    title: "Turn 180 — 180-Day Faith & Discipline Challenge · Mind. Body. Soul.",
    description:
      "Turn 180 is a 180-day transformation challenge built on faith, iron, and silence. Five pillars — Daily Bread, Daily Consistency, Fat & Nasty, Your Book, Ultimate Warrior. Sign the Covenant.",
  });

  return (
    <div className="relative bg-black text-white min-h-screen">
      <Header />
      <main>
        {/* HERO */}
        <section className="relative overflow-hidden pt-32 md:pt-44 pb-24 md:pb-32">
          {/* Backdrop faded logo */}
          <div className="absolute inset-0 flex items-center justify-center opacity-[0.045] pointer-events-none">
            <Logo showWordmark={false} className="w-[90vw] max-w-[1400px] h-auto" />
          </div>
          {/* Grain */}
          <div className="absolute inset-0 grain-overlay opacity-[0.08] pointer-events-none" />

          <div className="relative max-w-[1500px] mx-auto px-6 md:px-10">
            <Link
              to="/"
              className="inline-flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-white/50 hover:text-white transition-colors mb-10"
            >
              <span className="w-6 h-px bg-silver-100" />
              Back to home
            </Link>

            <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
              <div className="col-span-12 md:col-span-8">
                <div className="flex items-center gap-3 mb-6">
                  <span className="font-mono text-[10px] tracking-[0.42em] text-silver-300">
                    §CHALLENGE
                  </span>
                  <span className="inline-flex items-center gap-2 px-2.5 py-1 border border-silver-100/40 rounded-full font-mono text-[9px] tracking-[0.32em] uppercase text-silver-100">
                    <span className="w-1.5 h-1.5 rounded-full bg-silver-100 animate-pulse" />
                    180 Days
                  </span>
                </div>

                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                  className="font-serif text-white leading-[0.86] tracking-[-0.03em] text-[24vw] md:text-[16vw] lg:text-[14vw]"
                >
                  TURN
                  <span className="italic text-silver-100"> 180</span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.3 }}
                  className="mt-8 font-serif italic text-silver-100 text-2xl md:text-4xl leading-[1.15]"
                >
                  Become the person God created you to be.
                </motion.p>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.5 }}
                className="col-span-12 md:col-span-4 md:pl-8 md:border-l border-white/10"
              >
                <p className="text-white/70 leading-[1.85] text-[15px] mb-6">
                  Turn 180 is a 180-day transformation challenge designed to
                  rebuild your Mind, Body, and Soul through discipline, faith,
                  physical training, and personal growth.
                </p>
                <div className="space-y-2 border-t border-white/10 pt-6">
                  <div className="flex items-baseline gap-3 font-serif italic text-silver-100">
                    <span className="font-mono not-italic text-[10px] tracking-[0.3em] text-silver-300 w-8">
                      NO
                    </span>
                    <span>shortcuts.</span>
                  </div>
                  <div className="flex items-baseline gap-3 font-serif italic text-silver-100">
                    <span className="font-mono not-italic text-[10px] tracking-[0.3em] text-silver-300 w-8">
                      NO
                    </span>
                    <span>excuses.</span>
                  </div>
                  <div className="flex items-baseline gap-3 font-serif italic text-silver-100">
                    <span className="font-mono not-italic text-[10px] tracking-[0.3em] text-silver-300 w-8">
                      NO
                    </span>
                    <span>quitting.</span>
                  </div>
                </div>
                <a
                  href="#commit"
                  className="mt-10 inline-flex items-center gap-3 px-6 py-4 rounded-full bg-silver-100 text-black hover:bg-white transition-all duration-500 group"
                >
                  <span className="text-[11px] tracking-[0.32em] uppercase font-medium">
                    Sign the Covenant
                  </span>
                  <span className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-500">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M1 6H11M11 6L6 1M11 6L6 11" stroke="currentColor" strokeWidth="1.2" />
                    </svg>
                  </span>
                </a>
              </motion.div>
            </div>

            {/* Stats strip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.7 }}
              className="mt-20 md:mt-28 grid grid-cols-3 border-t border-white/10 pt-10"
            >
              {[
                ["180", "Days"],
                ["5", "Pillars"],
                ["1", "Decision"],
              ].map(([v, l]) => (
                <div key={l} className="text-center md:text-left px-6 md:px-0">
                  <div className="font-serif text-silver-100 text-6xl md:text-8xl leading-none">
                    {v}
                  </div>
                  <div className="mt-3 font-mono text-[10px] tracking-[0.35em] text-white/50 uppercase">
                    {l}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Intro block */}
        <section className="relative py-20 md:py-28 border-y border-white/10 bg-black">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10 text-center">
            <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-4">
              §00 — THE PREMISE
            </div>
            <h2 className="font-serif text-white text-3xl md:text-5xl leading-[1.15] max-w-3xl mx-auto">
              This isn&apos;t another fitness program.
              <br />
              <span className="italic text-silver-100">
                It&apos;s a complete lifestyle transformation.
              </span>
            </h2>
            <p className="mt-8 text-white/60 max-w-2xl mx-auto leading-[1.85] text-[15px]">
              Over the next 180 days you&apos;ll be challenged physically,
              mentally, emotionally, and spiritually until discipline becomes
              your identity.
            </p>
          </div>
        </section>

        {/* 5 Pillars */}
        <section className="relative py-24 md:py-32">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9 }}
              className="flex items-end justify-between mb-16 md:mb-20 border-b border-white/10 pb-8"
            >
              <div>
                <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-3">
                  §01 — FIVE PILLARS
                </div>
                <h2 className="font-serif text-[10vw] md:text-[5vw] leading-[0.95] tracking-[-0.02em]">
                  The Five Pillars of
                  <br />
                  <span className="italic text-silver-100">Turn 180.</span>
                </h2>
              </div>
              <div className="hidden md:block font-mono text-[10px] tracking-[0.32em] text-white/40">
                I — V
              </div>
            </motion.div>

            <div className="space-y-20 md:space-y-28">
              {PILLARS.map((p, i) => (
                <motion.article
                  key={p.n}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-120px" }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="grid grid-cols-12 gap-6 md:gap-12 items-start"
                >
                  {/* Left: numeral + icon */}
                  <div className="col-span-12 md:col-span-3">
                    <div className="flex items-start gap-4">
                      <div className="font-serif italic text-silver-200 text-[80px] md:text-[120px] leading-none">
                        {p.n}
                      </div>
                      <div className="mt-4 w-14 h-14 rounded-full border border-silver-100/30 flex items-center justify-center bg-white/[0.03]">
                        <p.Icon className="h-6 w-6 text-silver-100" strokeWidth={1.3} />
                      </div>
                    </div>
                    <div className="mt-3 font-mono text-[10px] tracking-[0.35em] text-silver-300/60">
                      PILLAR
                    </div>
                  </div>

                  {/* Middle: title + body */}
                  <div className="col-span-12 md:col-span-5">
                    <h3 className="font-serif text-white text-[8vw] md:text-[3.2vw] leading-[1.02] tracking-[-0.01em]">
                      {p.title}
                    </h3>
                    <p className="mt-4 font-serif italic text-silver-100 text-lg md:text-xl leading-[1.5]">
                      &ldquo;{p.quote}&rdquo;
                    </p>
                    <p className="mt-6 text-white/70 leading-[1.85] text-[14px] md:text-[15px]">
                      <span className="text-silver-100">{p.lead} </span>
                      {p.body}
                    </p>
                  </div>

                  {/* Right: list */}
                  <div className="col-span-12 md:col-span-4">
                    <div className="border-l border-white/10 pl-6 md:pl-8">
                      <div className="font-mono text-[10px] tracking-[0.35em] text-silver-300 mb-4">
                        {p.list.label}
                      </div>
                      <ul className="space-y-2">
                        {p.list.items.map((it, idx) => (
                          <li
                            key={idx}
                            className="flex items-baseline gap-4 text-white/80 text-[14px]"
                          >
                            <span className="font-mono text-[9px] tracking-[0.3em] text-silver-300/60 w-6">
                              {String(idx + 1).padStart(2, "0")}
                            </span>
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* What You'll Gain */}
        <section className="relative py-24 md:py-32 border-y border-white/10 bg-black">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9 }}
              className="mb-16 md:mb-20 text-center"
            >
              <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-3">
                §02 — WHAT YOU WILL GAIN
              </div>
              <h2 className="font-serif text-[10vw] md:text-[5vw] leading-[0.95] tracking-[-0.02em] text-white">
                Four <span className="italic text-silver-100">dimensions.</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/8">
              {GAINS.map((g, gi) => (
                <motion.div
                  key={g.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{
                    duration: 0.7,
                    delay: gi * 0.06,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="bg-black p-8 md:p-10 min-h-[280px]"
                >
                  <div className="font-mono text-[10px] tracking-[0.4em] text-silver-300 mb-4">
                    0{gi + 1} · CATEGORY
                  </div>
                  <h3 className="font-serif text-white text-3xl md:text-4xl mb-6">
                    {g.label}
                  </h3>
                  <ul className="space-y-2">
                    {g.items.map((it) => (
                      <li
                        key={it}
                        className="flex items-baseline gap-3 text-white/70 text-[14px]"
                      >
                        <span className="w-1 h-1 rounded-full bg-silver-100" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Covenant */}
        <section id="covenant" className="relative py-24 md:py-36 overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center opacity-[0.045] pointer-events-none">
            <Logo showWordmark={false} className="w-[80vw] max-w-[1200px] h-auto" />
          </div>
          <div className="relative max-w-[1100px] mx-auto px-6 md:px-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="text-center mb-14"
            >
              <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-4">
                §03 — THE COVENANT
              </div>
              <h2 className="font-serif text-white text-[9vw] md:text-[4.4vw] leading-[1.02] tracking-[-0.02em]">
                The Turn 180 <span className="italic text-silver-100">Covenant.</span>
              </h2>
            </motion.div>

            <ol className="relative border-l border-white/10 pl-8 md:pl-14 md:mx-6 space-y-3">
              {COVENANT.map((line, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -18, filter: "blur(3px)" }}
                  whileInView={{ opacity: 1, x: 0, filter: "blur(0)" }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{
                    duration: 0.7,
                    delay: Math.min(i * 0.08, 0.7),
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="relative font-serif text-white text-[5.4vw] md:text-[2vw] leading-[1.35]"
                >
                  <span className="absolute -left-8 md:-left-14 top-2 md:top-3 font-mono text-[10px] tracking-[0.3em] text-silver-300/70 w-8 md:w-14 text-right pr-2">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {line}
                </motion.li>
              ))}
            </ol>
          </div>
        </section>

        {/* Are you ready? + Commit */}
        <section
          id="commit"
          className="relative py-24 md:py-32 border-t border-white/10 bg-black"
        >
          <div className="max-w-[1300px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-14 items-start">
            <div className="col-span-12 md:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9 }}
              >
                <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-3">
                  §04 — READY?
                </div>
                <h2 className="font-serif text-white text-[12vw] md:text-[6vw] leading-[0.9] tracking-[-0.02em]">
                  180 <span className="italic text-silver-100">Days.</span>
                </h2>
                <h3 className="font-serif italic text-silver-100 text-[8vw] md:text-[3vw] leading-[1.05] mt-2">
                  One decision.
                </h3>
                <h3 className="font-serif text-white text-[8vw] md:text-[3vw] leading-[1.05]">
                  A lifetime of change.
                </h3>

                <div className="mt-10 pt-8 border-t border-white/10 flex items-center gap-4">
                  <Logo showWordmark={false} className="h-10 w-10" />
                  <div className="font-mono text-[10px] tracking-[0.4em] text-silver-300">
                    TURN 180 · MIND. BODY. SOUL.
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="col-span-12 md:col-span-7">
              <CommitForm />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Turn180;
